import { ErrorBoundary } from "react-error-boundary"
import { Outlet, useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { GlobalInviteUserDialog } from '@/components/dialogs/GlobalInvitationDialog.jsx'
import { useDashboardLoader } from '@/hooks/useDashboardLoader.jsx'
import { toggleInvitationDialog, useInviteMember } from '@/hooks/useInviteMember.jsx'
import { MainLoader } from '@/components/loader/MainLoader.jsx'
// import { NavBar } from '@/layouts/dashboard/nav/NavBar.jsx'
import { MainSidebar } from '@/layouts/dashboard/nav/MainSidebar.jsx'
import { useEffect, useState } from 'react'
import { handleError } from '@/services/errorHandler.js'
import { AppErrorBoundary } from '@/components/app-error-boundry/AppErrorBoundary.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { useDashboardContext } from "@/context-state/DashboardContext";
import { useCurrentAccount } from "@/hooks/useCurrentAccount"
import { toggleAppBanner, useAppBanner } from "@/hooks/useAppBanner"
import { AppBanner } from "@/components/app-banner/AppBanner"
import { togglePricingPlansDialog } from "@/hooks/usePricingPlans"
import { CreativeAiChatDrawer } from '@/components/creative-ask-ai/CreativeAiChatDrawer.jsx'
import { AdAccountsDialog } from '@/components/ad-account-select/AdAccountsDialog.jsx'
import { IntegrationProviders } from "@/consts/index.js"
import { useTikTokConnection } from "@/hooks/useTikTokConnection.jsx"
import { useGoogleAdsConnection } from "@/hooks/useGoogleAdsConnection.jsx"
import { useLinkedInConnection } from '@/hooks/useLinkedInConnection.jsx'
import { useCurrentSpace } from "@/hooks/useCurrentSpace"
// import { FloatingAiSearchBar } from "@/pages/dashboard/creative/FloatingAiSearchBar"
import { WatchListProvider } from "@/context-state/WatchListContext.jsx"
import { BoardsProvider } from "@/context-state/BoardsContext.jsx"
import { ViewsListProvider } from "@/context-state/ViewsListContext.jsx"

// import { driver } from "driver.js";
// import "driver.js/dist/driver.css";

export default function DashboardLayout() {

    const { user, updateUserData } = useAuthContext()
    const { account } = useCurrentAccount()
    const isLoading = useDashboardLoader()
    const isInviteMemberDialogOpen = useInviteMember()
    const [isNavbarExpanded, setIsNavbarExpanded] = useState(user.testUser ? false : true)
    const appBannerType = useAppBanner()
    const { scrollContext } = useDashboardContext()
    const { scrollContainerRef } = scrollContext
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const { space } = useCurrentSpace()
    const { handleTikTokAdAccountSelection } = useTikTokConnection()
    const { handleGoogleAdAccountSelection } = useGoogleAdsConnection()
    const { handleLinkedInAdAccountSelection } = useLinkedInConnection()
    const [isTikTokAdAccountsDialogOpen, setIsTikTokAdAccountsDialogOpen] = useState(false)
    const [isGoogleAdAccountsDialogOpen, setIsGoogleAdAccountsDialogOpen] = useState(false)
    const [isLinkedInAdAccountsDialogOpen, setIsLinkedInAdAccountsDialogOpen] = useState(false)

    const openPricing = searchParams.get('openPricing') // Meaning we intend to override the default behavior of the auth flow. we do it for colab team to be able to log in behalf of users.
    const closeSidebar = searchParams.get('closeSidebar')


    // const updateUserSafely = async () => {
    //     try {
    //         if (user.featureDiscovery?.adsQaProductTour) {
    //             return
    //         }
    //         await API.updateUser(user._id, { featureDiscovery: { adsQaProductTour: true } })
    //         updateUserData({ featureDiscovery: { adsQaProductTour: true } })
    //     } catch (e) {
    //         handleError(e)
    //     }
    // }

    // Force to navigate to onboarding if the account requires it. Onboarding now
    // lives outside the dashboard subtree (its own lean route), so target it by
    // absolute path.
    useEffect(() => {
        if (account?.requiresOnboarding && space?._id) {
            navigate(`/account/${account._id}/space/${space._id}/onboarding`)
            return
        }

        if (openPricing) {
            togglePricingPlansDialog(true)
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete('openPricing')
            setSearchParams(newSearchParams, { replace: true })
            return
        }

    }, [account])


    useEffect(() => {
        if (sessionStorage.getItem('forceTikTokAccountSelection') === 'true') {
            setIsTikTokAdAccountsDialogOpen(true)
            sessionStorage.removeItem('forceTikTokAccountSelection')
        }
        if (sessionStorage.getItem('forceGoogleAccountSelection') === 'true') {
            setIsGoogleAdAccountsDialogOpen(true)
            sessionStorage.removeItem('forceGoogleAccountSelection')
        }
        if (sessionStorage.getItem('forceLinkedInAccountSelection') === 'true') {
            sessionStorage.removeItem('forceLinkedInAccountSelection')
            setIsLinkedInAdAccountsDialogOpen(true)
        }
    }, [])


    useEffect(() => {
        if (closeSidebar) {
            setIsNavbarExpanded(false)
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete('closeSidebar')
            setSearchParams(newSearchParams, { replace: true })
        }
    }, [closeSidebar])

    // const isCreativePath = location.pathname.includes('creative')
    // Intelligence pages render their own page chrome (gradient headers,
    // full-bleed strips, sticky filter bars). The dashboard's outer p-4
    // padding creates an unwanted side-gap there.
    const isIntelligencePath = location.pathname.includes('/intelligence')

    return (
        <ViewsListProvider>
            <WatchListProvider>
                <BoardsProvider>
                    <div className="h-screen relative">

                        {/* Overlay Message */}
                        <AppBanner
                            type={appBannerType}
                            onClose={() => toggleAppBanner(null)}
                        />

                        <ErrorBoundary
                            fallback={<AppErrorBoundary />}
                            onError={(e, info) => handleError(e, { info, context: 'ErrorBoundary' })}
                        >
                            <MainSidebar
                                isExpanded={isNavbarExpanded}
                                setIsExpanded={setIsNavbarExpanded}
                                isTestUser={user.testUser}
                            />
                        </ErrorBoundary>
                        <main className={`transition-all duration-300 h-full bg-main-surface bg-center bg-cover bg-no-repeat overflow-hidden relative ${isNavbarExpanded ? 'pl-64' : 'pl-0'} `}>

                            {/* Dashboard Global Components */}
                            {isLoading && <MainLoader className="absolute z-[9999] top-0 left-0 bg-background/70 h-full" />}

                            <GlobalInviteUserDialog open={isInviteMemberDialogOpen} closeDialog={() => toggleInvitationDialog(false)} />

                            {/* Creative AI Chat Drawer - Global */}
                            <CreativeAiChatDrawer />
                            {/* Dashboard Global Components Ends */}

                            <div className={`${isIntelligencePath ? '' : 'p-4 px-4'} h-full overflow-y-scroll`} ref={scrollContainerRef}>
                                <ErrorBoundary
                                    fallback={<AppErrorBoundary />}
                                    onError={(e, info) => handleError(e, { info, context: 'ErrorBoundary' })}
                                >
                                    <Outlet />
                                </ErrorBoundary>

                            </div>

                            {/* {isCreativePath && <FloatingAiSearchBar />} */}

                        </main>
                        {/* <DashboardFooter /> */}
                        {/* Isolated so a broken dialog disappears instead of crashing the whole dashboard */}
                        <ErrorBoundary
                            fallback={null}
                            onError={(e, info) => handleError(e, { info, context: 'ErrorBoundary:LayoutAdAccountsDialogs' })}
                        >
                            <AdAccountsDialog
                                isDialogOpen={isTikTokAdAccountsDialogOpen}
                                setIsDialogOpen={setIsTikTokAdAccountsDialogOpen}
                                title="Choose TikTok Ad Accounts"
                                description="Select TikTok ad accounts to add to this Space"
                                onConfirm={handleTikTokAdAccountSelection}
                                chosenAdAccountsIds={space?.adAccounts?.filter(acc => acc.provider === 'tiktok_ads').map(adAccount => adAccount.id)}
                                providerOverride={IntegrationProviders.TIKTOK_ADS}
                            />
                            <AdAccountsDialog
                                isDialogOpen={isGoogleAdAccountsDialogOpen}
                                setIsDialogOpen={setIsGoogleAdAccountsDialogOpen}
                                title="Choose Google Ads Accounts"
                                description="Select Google Ads accounts to add to this Space"
                                onConfirm={handleGoogleAdAccountSelection}
                                chosenAdAccountsIds={space?.adAccounts?.filter(acc => acc.provider === 'GOOGLE_ADS').map(adAccount => adAccount.id)}
                                providerOverride={IntegrationProviders.GOOGLE_ADS}
                            />
                            <AdAccountsDialog
                                isDialogOpen={isLinkedInAdAccountsDialogOpen}
                                setIsDialogOpen={setIsLinkedInAdAccountsDialogOpen}
                                title="Choose LinkedIn Ad Accounts"
                                description="Select LinkedIn ad accounts to add to this Space"
                                onConfirm={handleLinkedInAdAccountSelection}
                                providerOverride={IntegrationProviders.LINKEDIN_ADS}
                            />
                        </ErrorBoundary>
                    </div>
                </BoardsProvider>
            </WatchListProvider>
        </ViewsListProvider>
    )
}
